import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react'

interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[]
  icon?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  error?: boolean
  placeholder?: string
  label?: string
  description?: string
  errorMessage?: string
  required?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  options,
  icon,
  size = 'md',
  error,
  placeholder,
  label,
  description,
  errorMessage,
  required,
  className,
  ...props
}, ref) => {
  const sizeClass = size === 'sm' ? 'v3-select-sm' : size === 'lg' ? 'v3-select-lg' : ''

  const select = (
    <div className={`v3-select-wrap ${error || errorMessage ? 'v3-select-error' : ''} ${sizeClass} ${className || ''}`}>
      {icon && <span className="v3-select-icon">{icon}</span>}
      <select ref={ref} className="v3-select" aria-invalid={error || !!errorMessage} {...props}>
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
        ))}
      </select>
      <span className="v3-select-chevron">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </span>
    </div>
  )

  if (label || errorMessage) {
    return (
      <div className="v3-field">
        {label && (
          <label className="v3-field-label">
            {label}
            {required && <span className="v3-field-required">*</span>}
          </label>
        )}
        {description && <p className="v3-field-desc">{description}</p>}
        {select}
        {errorMessage && <p className="v3-field-error">{errorMessage}</p>}
      </div>
    )
  }

  return select
})

Select.displayName = 'Select'
